import React, { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { Loader2} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import RuleBuilderDragDrop from './RuleBuilderDragDrop';
import WorkflowPublicationPanel from './WorkflowPublicationPanel';
import { RulesListView } from './NocodeRulesListView';
import { TestPanel } from './NocodeTestPanel';

export const VersionEditor = ({ version, simulatorId: simulatorId, token, lang, permissions, userRole, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('restrictions');
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [showRuleBuilder, setShowRuleBuilder] = useState(false);

  useEffect(() => {
    fetchRules();
  }, [version.id]);

  const fetchRules = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/versions/${version.id}/rules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRules(data.rules || []);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'restrictions', label: '📋 Restrictions' },
    { id: 'parameters', label: '⚙️ Paramètres' },
    { id: 'test', label: '🧪 Test' },
    { id: 'publish', label: '🚀 Publication' }
  ];

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Brouillon' },
      submitted: { color: 'bg-yellow-100 text-yellow-800', label: 'En review' },
      approved: { color: 'bg-blue-100 text-blue-800', label: 'Approuvé' },
      published: { color: 'bg-green-100 text-green-800', label: 'Publié' },
      archived: { color: 'bg-gray-100 text-gray-500', label: 'Archivé' }
    };
    const c = config[status] || config.draft;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const isDraft = version.status === 'draft';

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Version header */}
      <div className="px-4 py-3 border-b flex items-center justify-between bg-white">
        <div className="flex items-center gap-3">
          <span className="font-bold text-lg">{version.version_number}</span>
          {getStatusBadge(version.status)}
          <span className="text-sm text-gray-500">{version.name}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b bg-gray-50">
        <div className="flex">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        ) : showRuleBuilder || editingRule ? (
          <RuleBuilderDragDrop
            rule={editingRule}
            versionId={version.id}
            token={token}
            onSave={() => {
              setShowRuleBuilder(false);
              setEditingRule(null);
              fetchRules();
            }}
            onCancel={() => {
              setShowRuleBuilder(false);
              setEditingRule(null);
            }}
            lang={lang}
          />
        ) : activeTab === 'restrictions' ? (
          <RulesListView 
            rules={rules} 
            token={token} 
            versionId={version.id}
            isDraft={isDraft}
            onUpdate={fetchRules}
            onEditRule={(rule) => setEditingRule(rule)}
            onNewRule={() => setShowRuleBuilder(true)}
          />
        ) : activeTab === 'test' ? (
          <TestPanel versionId={version.id} token={token} />
        ) : activeTab === 'publish' ? (
          <WorkflowPublicationPanel
            version={version}
            token={token}
            userRole={userRole}
            permissions={permissions}
            onVersionUpdate={onUpdate}
            lang={lang}
          />
        ) : (
          <div className="text-center py-8 text-gray-500">
            Section {activeTab} en construction...
          </div>
        )}
      </div>
    </div>
  );
};

// Rules List View
